import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { categoriesApi } from '../../services/mybudgetApi';
import { Category } from '../../types';
import { Card } from '../../components/shared/Card';
import { Loading } from '../../components/shared/Loading';
import { ActionButton } from '../../components/shared/ActionButton';
import { Input } from '../../components/shared/Input';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { CategoryBadge } from '../../components/shared/CategoryBadge';
import { useToaster } from '../../components/shared/Toaster';
import { useCategories } from '../../contexts/CategoriesContext';

const PRESET_COLORS = [
  '#8b5cf6',
  '#f472b6',
  '#f59e0b',
  '#38bdf8',
  '#a78bfa',
  '#34d399',
  '#fb7185',
  '#60a5fa',
  '#94a3b8',
  '#f97316',
];

export function CategoriesPage() {
  const { categories, loading, refresh } = useCategories();
  const { addToast } = useToaster();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', color: PRESET_COLORS[0] });

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', color: PRESET_COLORS[0] });
    setModalOpen(true);
  };

  const openEdit = (category: Category) => {
    setEditing(category);
    setForm({ name: category.name, color: category.color });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await categoriesApi.update(editing.id, form);
        addToast({ type: 'success', message: 'Categoria atualizada!' });
      } else {
        await categoriesApi.create(form);
        addToast({ type: 'success', message: 'Categoria criada!' });
      }
      setModalOpen(false);
      await refresh();
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao salvar',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category: Category) => {
    if (!window.confirm(`Excluir a categoria "${category.name}"?`)) return;
    try {
      await categoriesApi.delete(category.id);
      addToast({ type: 'success', message: 'Categoria removida' });
      await refresh();
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao remover',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-100">Categorias</h2>
          <p className="text-sm text-gray-400">
            Organize despesas e projetos com categorias personalizadas
          </p>
        </div>
        <ActionButton onClick={openCreate}>
          <AddIcon fontSize="small" />
          Nova categoria
        </ActionButton>
      </div>

      {loading ? (
        <Loading />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((category) => (
            <Card key={category.id} className="!p-0">
              <div className="flex items-center justify-between px-5 py-4">
                <div className="space-y-2">
                  <CategoryBadge category={category} />
                  {category.isDefault && (
                    <p className="text-xs text-gray-500">Padrão do sistema</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => openEdit(category)}
                    className="text-gray-500 hover:text-accent"
                  >
                    <EditIcon fontSize="small" />
                  </button>
                  {!category.isDefault && (
                    <button
                      onClick={() => handleDelete(category)}
                      className="text-gray-500 hover:text-red-400"
                    >
                      <DeleteIcon fontSize="small" />
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Editar categoria' : 'Nova categoria'}
        footer={
          <ModalActions
            onCancel={() => setModalOpen(false)}
            onConfirm={handleSave}
            loading={saving}
          />
        }
      >
        <div className="space-y-4">
          <Input
            label="Nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <div>
            <p className="mb-2 text-sm font-medium text-gray-300">Cor</p>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setForm({ ...form, color })}
                  className={`h-8 w-8 rounded-full border-2 transition ${
                    form.color === color ? 'border-white scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
